import React, { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HOME_THEME } from "./theme";

interface ScrollRowProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  ariaLabel?: string;
}

const arrowStyle: React.CSSProperties = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: `1px solid ${HOME_THEME.border}`,
  background: HOME_THEME.surface,
  color: HOME_THEME.heroHeading,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

/**
 * Horizontal scroller with the section heading and the arrow buttons on the
 * SAME row. The heading text stays centered; arrows float to the far right.
 * (All styles are inline so this does not depend on an external stylesheet.)
 */
const ScrollRow: React.FC<ScrollRowProps> = ({ children, header, ariaLabel }) => {
  const ref = useRef<HTMLDivElement>(null);

  const scroll = (direction: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(300, el.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "14px",
        }}
      >
        {/* Invisible spacer matching the arrow width keeps the heading truly centered */}
        <div style={{ width: "80px", flexShrink: 0 }} aria-hidden />

        <div
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            maxWidth: "720px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          {header}
        </div>

        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
          <button
            type="button"
            aria-label="Scroll left"
            onClick={() => scroll(-1)}
            style={arrowStyle}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Scroll right"
            onClick={() => scroll(1)}
            style={arrowStyle}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div
        ref={ref}
        aria-label={ariaLabel}
        style={{
          display: "flex",
          gap: "14px",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          paddingBottom: "6px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ScrollRow;
